import { useEffect, useRef, useState } from 'react'
import { Box, Chip, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import Swal from 'sweetalert2'
import { useAppServices } from 'hook/services'
import NotificationFormModal from '../../NotificationFormModal'
import { AUDIENCES, BLUE, TYPES, blueSwitchSx, formatSydney, statusMeta } from '../../../utils'

const emptyFilters = { status: '', type: '', audience: '', search: '' }

const labelFor = (list, value) => list.find((item) => item.value === value)?.label || value

const AppData = () => {
  const [notifications, setNotifications] = useState([])
  const [loader, setLoader] = useState(false)
  const [filters, setFilters] = useState(emptyFilters)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, totalPages: 1, totalUsers: 0 })
  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] })
  const AppService = useAppServices()

  // Filters live in a ref too, so paging keeps whatever is currently selected.
  const stateRef = useRef({ filters: emptyFilters, page: 1, limit: 20 })

  const fetchNotifications = async ({ filters: next = stateRef.current.filters, page = 1, limit = stateRef.current.limit } = {}) => {
    stateRef.current = { filters: next, page, limit }
    setLoader(true)

    const params = new URLSearchParams({ page, limit })
    Object.entries(next).forEach(([key, value]) => value && params.set(key, value))

    const { response } = await AppService.notifications.get({ query: params.toString() })

    if (response) {
      setNotifications(response.notifications || [])
      setPagination({
        page: response.pagination?.page || page,
        limit: response.pagination?.limit || limit,
        totalPages: response.pagination?.pages || 1,
        totalUsers: response.pagination?.total || 0,
      })
    }

    setLoader(false)
  }

  useEffect(() => {
    fetchNotifications()
  }, [])

  // Typing in search shouldn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchNotifications({ filters, page: 1 })
    }, 350)

    return () => clearTimeout(timer)
  }, [filters.status, filters.type, filters.audience, filters.search])

  const handleRefresh = () =>
    fetchNotifications({ filters, page: stateRef.current.page, limit: stateRef.current.limit })

  const handlePageChange = (page) => fetchNotifications({ filters, page })
  const handleLimitChange = (limit) => fetchNotifications({ filters, page: 1, limit })

  // Optimistic pause/resume — revert the switch if the API says no.
  const handleToggle = async (id, isActive) => {
    setNotifications((prev) => prev.map((item) => (item._id === id ? { ...item, isActive } : item)))

    const { response } = await AppService.notifications.toggle({ payload: { id, isActive } })

    if (!response) {
      setNotifications((prev) =>
        prev.map((item) => (item._id === id ? { ...item, isActive: !isActive } : item))
      )
      return
    }

    // Status is derived from isActive + the window, so refetch for the correct chip.
    handleRefresh()
  }

  const handleDelete = async (notification) => {
    const confirm = await Swal.fire({
      title: 'Delete this notification?',
      text: notification.name || notification.title || 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it',
    })

    if (!confirm.isConfirmed) return

    const { response } = await AppService.notifications.delete({ payload: { id: notification._id } })

    if (response) {
      handleRefresh()
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1500, showConfirmButton: false })
    }
  }

  useEffect(() => {
    const columns = [
      { Header: 'Notification', accessor: 'notification', width: '26%' },
      { Header: 'Type', accessor: 'type', width: '10%' },
      { Header: 'Audience', accessor: 'audience', width: '12%' },
      { Header: 'Schedule (Sydney)', accessor: 'schedule', width: '18%' },
      { Header: 'Status', accessor: 'status', width: '9%', align: 'center' },
      { Header: 'Performance', accessor: 'stats', width: '11%' },
      { Header: 'Active', accessor: 'active', width: '7%', align: 'center', disableSortBy: true },
      { Header: 'Actions', accessor: 'actions', width: '7%', align: 'center', disableSortBy: true },
    ]

    const rows = notifications.map((item) => {
      const status = statusMeta(item.status)

      return {
        notification: (
          <Box sx={{ maxWidth: 300 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
              {item.name || item.title || 'Untitled'}
            </Typography>
            {item.title && item.name && (
              <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>{item.title}</Typography>
            )}
            <Tooltip title={item.cta?.link || ''}>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  color: BLUE.main,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.cta?.enabled && item.cta?.link ? `→ ${item.cta.link}` : 'No CTA'}
              </Typography>
            </Tooltip>
          </Box>
        ),
        type: (
          <Box>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, textTransform: 'capitalize' }}>
              {labelFor(TYPES, item.type)}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{item.placement}</Typography>
          </Box>
        ),
        audience: (
          <Chip
            label={labelFor(AUDIENCES, item.audience)}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.68rem',
              fontWeight: 600,
              color: BLUE.main,
              borderColor: BLUE.line,
              backgroundColor: BLUE.soft,
            }}
          />
        ),
        schedule: (
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>
              {formatSydney(item.startAt)}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>
              to {formatSydney(item.endAt)}
            </Typography>
          </Box>
        ),
        status: (
          <Chip
            label={status.label}
            size="small"
            sx={{ fontSize: '0.68rem', fontWeight: 700, ...status.sx }}
          />
        ),
        stats: (
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>
              {item.stats?.impressions || 0} shown · {item.stats?.clicks || 0} clicks
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#999' }}>
              {item.stats?.dismissals || 0} dismissed
            </Typography>
          </Box>
        ),
        active: (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={item.isActive ? 'Running on schedule' : 'Paused'}>
              <Switch
                checked={Boolean(item.isActive)}
                sx={blueSwitchSx}
                onChange={(e) => handleToggle(item._id, e.target.checked)}
              />
            </Tooltip>
          </Box>
        ),
        actions: (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <NotificationFormModal notification={item} handleRefresh={handleRefresh} />
            <Tooltip title="Delete">
              <IconButton size="small" sx={{ color: '#d33' }} onClick={() => handleDelete(item)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      }
    })

    setDataTableData({ columns, rows })
  }, [notifications])

  return {
    dataTableData,
    loader,
    filters,
    setFilters,
    pagination,
    handleRefresh,
    handlePageChange,
    handleLimitChange,
  }
}

export default AppData
